import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3 } from "lucide-react";
import type { Participant } from "@/api/types";
import { UploadAlertDialog } from "@/components/sensor-charts/upload-alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Spinner, ErrorDisplay, EmptyState } from "@/components/loading";
import { useParticipants } from "@/api/hooks";
import { relativeTime, isActive } from "@/lib/format-date";
import {
  getUploadAlertBadgeLabel,
  hasUploadAlert,
} from "@/lib/upload-alerts";

export default function ParticipantsPage() {
  const { data, isLoading, isError, error, refetch } = useParticipants();
  const navigate = useNavigate();
  const [selectedParticipant, setSelectedParticipant] =
    useState<Participant | null>(null);

  if (isLoading) return <Spinner />;
  if (isError)
    return <ErrorDisplay message={error.message} onRetry={() => refetch()} />;

  return (
    <div className="p-4 space-y-6 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-[#1E293B]">Participants</h1>
        <div className="flex items-center gap-3">
          {data && (
            <span className="text-sm text-muted-foreground">
              {data.count} total
            </span>
          )}
        </div>
      </div>

      {!data || data.data.length === 0 ? (
        <EmptyState message="No participants found" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Participant ID</TableHead>
              <TableHead>Total Uploads</TableHead>
              <TableHead>Devices</TableHead>
              <TableHead>App Version</TableHead>
              <TableHead>Missing Permissions</TableHead>
              <TableHead>Last Seen</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Analysis</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.data.map((participant) => (
              <TableRow
                key={participant.user_id}
                className="cursor-pointer"
                onClick={() => navigate(`/participants/${participant.user_id}`)}
              >
                <TableCell className="font-mono text-xs">
                  {participant.username || participant.user_id.slice(0, 8)}
                </TableCell>
                <TableCell>
                  {participant.total_uploads.toLocaleString()}
                </TableCell>
                <TableCell>
                  {participant.device_count ?? 0}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {participant.latest_app_version || "—"}
                </TableCell>
                <TableCell>
                  {participant.missing_permissions && participant.missing_permissions.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {participant.missing_permissions.map((permission) => (
                        <Badge
                          key={permission}
                          className="bg-rose-100 text-rose-900 border-rose-300"
                        >
                          {permission}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">None</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {participant.last_uploaded_at
                    ? relativeTime(participant.last_uploaded_at)
                    : "—"}
                </TableCell>
                <TableCell>
                  {hasUploadAlert(participant) ? (
                    <Badge className="bg-amber-100 text-amber-900 border-amber-300">
                      {getUploadAlertBadgeLabel(participant)}
                    </Badge>
                  ) : participant.last_uploaded_at &&
                    isActive(participant.last_uploaded_at) ? (
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Inactive</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedParticipant(participant);
                    }}
                  >
                    <BarChart3 className="mr-1.5 size-3.5" />
                    Analyze
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <UploadAlertDialog
        participant={selectedParticipant}
        open={selectedParticipant !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setSelectedParticipant(null);
        }}
      />
    </div>
  );
}
